import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DEBOUNCE_MS } from '@/components/features/UsersList/constants';
import AutocompleteContainer from '@/components/shared/Autocomplete/components/AutocompleteContainer';
import AutocompleteFilterButton from '@/components/shared/Autocomplete/components/AutocompleteFilterButton';
import AutocompleteWithLabel from '@/components/shared/Autocomplete/components/AutocompleteWithLabel';
import { useBudgetGroupsAutocomplete } from '@/hooks/autocomplete/useBudgetGroupsAutocomplete';
import { type UserAutocompleteResult, useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';
import { components } from '@/shared/api/openapi';

type BudgetGroup = components['schemas']['GetBudgetGroupsEndpoint_BudgetResponse'];

export interface BudgetsFiltersAutocompleteProps {
  organizationId: number;
  disabled?: boolean;
}

const parseGroupsParam = (searchParams: URLSearchParams): BudgetGroup[] => {
  const groupsParam = searchParams.get('groups');
  if (!groupsParam) return [];
  return groupsParam
    .split(',')
    .filter(Boolean)
    .map((name) => ({ name }));
};

const parseEmailsParam = (searchParams: URLSearchParams): UserAutocompleteResult[] => {
  const emailParam = searchParams.get('emails');
  if (!emailParam) return [];
  return emailParam
    .split(',')
    .filter(Boolean)
    .map((email) => ({ email }));
};

function BudgetsFiltersAutocomplete({ organizationId, disabled }: BudgetsFiltersAutocompleteProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedGroups, setSelectedGroups] = useState<BudgetGroup[]>(parseGroupsParam(searchParams));
  const [selectedEmails, setSelectedEmails] = useState<UserAutocompleteResult[]>(parseEmailsParam(searchParams));

  const filterButtonRef = useRef<HTMLButtonElement | null>(null);

  const [groupInputValue, setGroupInputValue] = useState('');
  const [groupDebouncedValue, setGroupDebouncedValue] = useState('');

  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailDebouncedValue, setEmailDebouncedValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setGroupDebouncedValue(groupInputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [groupInputValue]);

  useEffect(() => {
    const timer = setTimeout(() => setEmailDebouncedValue(emailInputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [emailInputValue]);

  const {
    data: groupsData,
    isLoading: isGroupsLoading,
    isRefetching: isGroupsRefetching,
  } = useBudgetGroupsAutocomplete({
    organizationId,
    filter: groupDebouncedValue,
    enabled: !disabled,
  });

  const {
    data: emailsData,
    isLoading: isEmailsLoading,
    isRefetching: isEmailsRefetching,
  } = useUsersAutocomplete({
    organizationId,
    filter: emailDebouncedValue,
    enabled: !disabled,
  });

  const handleGroupsChange = (_: React.SyntheticEvent, groups: BudgetGroup[]) => {
    setSelectedGroups(groups);
    if (groups.length === 0) {
      const params = new URLSearchParams(searchParams);
      params.delete('groups');
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  };

  const handleEmailsChange = (_: React.SyntheticEvent, emails: UserAutocompleteResult[]) => {
    setSelectedEmails(emails);
    if (emails.length === 0) {
      const params = new URLSearchParams(searchParams);
      params.delete('emails');
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  };

  const handleFilterApply = () => {
    const params = new URLSearchParams(searchParams);

    if (selectedGroups.length > 0) {
      const groupNames = selectedGroups.map((g) => g.name).filter((n): n is string => Boolean(n));
      if (groupNames.length > 0) {
        params.set('groups', groupNames.join(','));
      } else {
        params.delete('groups');
      }
    } else {
      params.delete('groups');
    }

    if (selectedEmails.length > 0) {
      const emails = selectedEmails.map((e) => e.email).filter((e): e is string => Boolean(e));
      if (emails.length > 0) {
        params.set('emails', emails.join(','));
      } else {
        params.delete('emails');
      }
    } else {
      params.delete('emails');
    }

    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const isFilterDisabled = disabled || (selectedGroups.length === 0 && selectedEmails.length === 0);

  return (
    <AutocompleteContainer>
      <AutocompleteWithLabel<BudgetGroup>
        options={groupsData ?? []}
        value={selectedGroups}
        inputValue={groupInputValue}
        loading={isGroupsLoading || isGroupsRefetching}
        onChange={handleGroupsChange}
        onInputChange={(_, input) => setGroupInputValue(input)}
        getOptionLabel={(option) => option.name ?? ''}
        filterOptions={(x) => x}
        getOptionDisabled={(option) => selectedGroups.some((g) => g.name === option.name)}
        label='Filter by Budgets'
        disabled={disabled ?? false}
        testIdSuffix='budgets'
      />
      <AutocompleteWithLabel<UserAutocompleteResult>
        options={emailsData ?? []}
        value={selectedEmails}
        inputValue={emailInputValue}
        loading={isEmailsLoading || isEmailsRefetching}
        onChange={handleEmailsChange}
        onInputChange={(_, input) => setEmailInputValue(input)}
        getOptionLabel={(option) => option.email ?? ''}
        filterOptions={(x) => x}
        getOptionDisabled={(option) => selectedEmails.some((e) => e.email === option.email)}
        label='Filter by User Email'
        disabled={disabled ?? false}
        testIdSuffix='emails'
      />
      <AutocompleteFilterButton
        buttonLabel='Filter'
        buttonRef={filterButtonRef}
        onClick={handleFilterApply}
        isDisabled={isFilterDisabled}
      />
    </AutocompleteContainer>
  );
}

export default BudgetsFiltersAutocomplete;
